'use client';

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    label?: string;
    description?: string;
    indeterminate?: boolean;
  }
>(({ className, label, description, indeterminate, id, ...props }, ref) => {
  const generatedId = React.useId();
  const checkboxId = id || generatedId;

  return (
    <div className="flex items-start gap-3">
      <CheckboxPrimitive.Root
        ref={ref}
        id={checkboxId}
        className={cn(
          'peer h-5 w-5 shrink-0',
          // Ступень hairline («почти угол»), а НЕ card. Card = 20px на боксе
          // 20×20 браузер зажимает до 10px, то есть до идеального круга —
          // чекбокс стал бы неотличим от радио-кнопки рядом (RadioGroupItem,
          // тот же размер 20×20, rounded-pill). Квадратный чекбокс и круглое
          // радио — это разное поведение, и путать их формой нельзя.
          'rounded-hairline border border-[var(--border-default)]',
          'bg-transparent',
          'transition-all duration-150',
          // Focus
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-primary)]',
          // Hover
          'hover:border-[var(--primary)]',
          // Checked
          'data-[state=checked]:bg-[var(--primary)] data-[state=checked]:border-[var(--primary)]',
          'data-[state=indeterminate]:bg-[var(--primary)] data-[state=indeterminate]:border-[var(--primary)]',
          // Disabled
          'disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        checked={indeterminate ? 'indeterminate' : props.checked}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center text-white">
          {indeterminate ? (
            <Minus className="h-3.5 w-3.5" weight="bold" />
          ) : (
            <Check className="h-3.5 w-3.5" weight="bold" />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>

      {(label || description) && (
        <div className="grid gap-0.5 leading-none">
          {label && (
            <label
              htmlFor={checkboxId}
              className="text-sm font-medium text-[var(--text-primary)] cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {label}
            </label>
          )}
          {description && (
            <p className="text-xs text-[var(--text-tertiary)]">{description}</p>
          )}
        </div>
      )}
    </div>
  );
});
Checkbox.displayName = 'Checkbox';

export { Checkbox };

